import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TimelineComparison = ({ suggestedStart, suggestedEnd, actualStart, actualEnd, status, onUpdateActual, userRole, progress }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editedStart, setEditedStart] = React.useState(actualStart || '');
  const [editedEnd, setEditedEnd] = React.useState(actualEnd || '');

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const calculateDaysDifference = (date1, date2) => {
    if (!date1 || !date2) return null;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = d2 - d1;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const isDateLater = (actualDate, suggestedDate) => {
    if (!actualDate || !suggestedDate) return false;
    const actual = new Date(actualDate);
    const suggested = new Date(suggestedDate);
    return actual > suggested;
  };

  const isStartDateLate = isDateLater(actualStart, suggestedStart);
  const isEndDateLate = isDateLater(actualEnd, suggestedEnd);

  const getDelayStatus = () => {
    if (!suggestedEnd || !actualEnd) return null;
    const diff = calculateDaysDifference(suggestedEnd, actualEnd);
    if (diff > 0) return { type: 'delayed', days: diff };
    if (diff < 0) return { type: 'early', days: Math.abs(diff) };
    return { type: 'on-time', days: 0 };
  };

  const delayStatus = getDelayStatus();

  const handleSave = () => {
    onUpdateActual(editedStart, editedEnd);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedStart(actualStart || '');
    setEditedEnd(actualEnd || '');
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Timeline Tracking</h2>
        {userRole === 'subcontractor' && (status === 'ongoing' || (progress > 0 && progress < 100)) && !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-smooth min-h-[44px]"
          >
            <Icon name="Edit2" size={16} strokeWidth={2} />
            Update Dates
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Target" size={20} className="text-primary" strokeWidth={2} />
            <h3 className="text-base font-semibold text-foreground">Suggested Timeline</h3>
          </div>

          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground mb-1">Start Date</p>
                <p className="text-base font-semibold text-foreground">{formatDate(suggestedStart)}</p>
              </div>
              <Icon name="ArrowRight" size={20} className="text-muted-foreground" strokeWidth={2} />
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground mb-1">End Date</p>
                <p className="text-base font-semibold text-foreground">{formatDate(suggestedEnd)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Activity" size={20} className={isStartDateLate || isEndDateLate ? 'text-error' : 'text-success'} strokeWidth={2} />
            <h3 className="text-base font-semibold text-foreground">Actual Timeline</h3>
          </div>

          {isEditing ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Actual Start Date</label>
                <input
                  type="date"
                  value={editedStart}
                  onChange={(e) => setEditedStart(e?.target?.value)}
                  className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Actual End Date</label>
                <input
                  type="date"
                  value={editedEnd}
                  onChange={(e) => setEditedEnd(e?.target?.value)}
                  className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSave}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth min-h-[44px]"
                >
                  Save Changes
                </button>
                <button
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2 bg-muted text-foreground rounded-lg font-medium hover:bg-muted/80 transition-smooth min-h-[44px]"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className={`p-4 rounded-lg border ${isStartDateLate || isEndDateLate ? 'bg-error/5 border-error/20' : 'bg-success/5 border-success/20'}`}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground mb-1">Start Date</p>
                  <p className={`text-base font-semibold ${isStartDateLate ? 'text-error' : 'text-foreground'}`}>
                    {formatDate(actualStart)}
                  </p>
                </div>
                <Icon name="ArrowRight" size={20} className="text-muted-foreground" strokeWidth={2} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground mb-1">End Date</p>
                  <p className={`text-base font-semibold ${isEndDateLate ? 'text-error' : 'text-foreground'}`}>
                    {formatDate(actualEnd)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {delayStatus && (
        <div className={`mt-6 p-4 rounded-lg border ${
          delayStatus?.type === 'delayed' ? 'bg-error/5 border-error/20' :
          delayStatus?.type === 'early'? 'bg-success/5 border-success/20' : 'bg-primary/5 border-primary/20'
        }`}>
          <div className="flex items-start gap-3">
            <Icon 
              name={delayStatus?.type === 'delayed' ? 'AlertCircle' : delayStatus?.type === 'early' ? 'TrendingDown' : 'CheckCircle2'} 
              size={20} 
              className={
                delayStatus?.type === 'delayed' ? 'text-error' :
                delayStatus?.type === 'early'? 'text-success' : 'text-primary'
              }
              strokeWidth={2}
            />
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground mb-1">
                {delayStatus?.type === 'delayed' ? 'Phase Delayed' :
                 delayStatus?.type === 'early' ? 'Completed Early' :
                 'On Schedule'}
              </p>
              <p className="text-sm text-muted-foreground">
                {delayStatus?.type === 'delayed' && `This phase is ${delayStatus?.days} day${delayStatus?.days > 1 ? 's' : ''} behind schedule. Boss has been notified.`}
                {delayStatus?.type === 'early' && `This phase was completed ${delayStatus?.days} day${delayStatus?.days > 1 ? 's' : ''} ahead of schedule.`}
                {delayStatus?.type === 'on-time' && 'This phase was completed exactly on schedule.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineComparison;